import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, User, AuthResponse, OtpVerificationRequest } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface AuthStore extends AuthState {
  // Actions
  requestOtp: (phoneNumber: string) => Promise<void>;
  verifyOtp: (phoneNumber: string, otp: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  checkAuth: () => boolean;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      userId: null,
      userUid: null,
      userRole: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      requestOtp: async (phoneNumber: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await apiService.requestOtp(phoneNumber);
          set({ 
            isLoading: false,
            error: null 
          });
          toast.success('OTP sent successfully!');
        } catch (error: any) {
          const errorMessage = error.message || 'Failed to send OTP. Please try again.';
          set({ 
            isLoading: false, 
            error: errorMessage 
          });
          toast.error(errorMessage);
          throw error;
        }
      },

      verifyOtp: async (phoneNumber: string, otp: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await apiService.verifyOtp(phoneNumber, otp);
          
          set({
            user: response.user,
            userId: response.userId,
            // userUid holds the boz-auth access JWT (sent as Authorization: Bearer).
            userUid: response.token,
            userRole: response.role,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          
          toast.success(`Welcome, ${response.user.name}!`);
        } catch (error: any) {
          const errorMessage = error.message || 'OTP verification failed. Please try again.';
          set({ 
            isLoading: false, 
            error: errorMessage 
          });
          toast.error(errorMessage);
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        
        try {
          await apiService.logout();
        } catch (error) {
          // Continue with logout even if API call fails
          console.warn('Logout API call failed:', error);
        } finally {
          set({
            user: null,
            userId: null,
            userUid: null,
            userRole: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          
          toast.success('Logged out successfully');
        }
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...userData } });
        }
      },

      clearError: () => set({ error: null }),
      
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      checkAuth: () => {
        const userId = localStorage.getItem('user_id');
        const userUid = localStorage.getItem('user_uid');
        const userRole = localStorage.getItem('user_role');

        // boz-auth migration: 'user_uid' must now hold an RS256 JWT (header.payload.signature).
        // A pre-migration session holds a legacy opaque token (no dots) that the backend now
        // rejects with 401 — drop it so the user is cleanly routed to re-login instead of being
        // stuck "authenticated" with every request failing.
        if (userUid && userUid.split('.').length !== 3) {
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_uid');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_role');
          set({
            user: null,
            userId: null,
            userUid: null,
            userRole: null,
            isAuthenticated: false,
          });
          return false;
        }

        if (userId && userUid && userRole) {
          const { user } = get();
          if (!user) {
            // Restore user data from localStorage if available
            set({
              userId: parseInt(userId),
              userUid,
              userRole,
              isAuthenticated: true,
            });
          }
          return true;
        }
        return false;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        userId: state.userId,
        userUid: state.userUid,
        userRole: state.userRole,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
